/**
 * Collection Service
 *
 * Handles API calls for collections and records operations.
 * Part of the V4 Records API.
 */

import { fetchWithAuth } from '../utils/api';
import { VALID_DATE_RANGE_PRESETS } from '../components/v4/records/filter/constants/filter.constants';
import type {
  Collection,
  CollectionWithStats,
  CollectionListResponse,
  CollectionFilters,
  CollectionUpdateRequest,
  RecordsListResponse,
  RecordFilters,
  ConfirmComplaintResponse,
  ReclassifyRecordResponse,
} from './types';
import type { Record } from '../types/record';

const COLLECTION_BASE_PATH = '/v1/collections';
const RECORD_BASE_PATH = '/v1/records';

// =============================================================================
// Collection Functions
// =============================================================================

/**
 * List all collections for the authenticated user's organization
 *
 * @param filters - Optional filters (type, status)
 * @returns Promise<CollectionListResponse>
 */
export const listCollections = async (
  filters?: CollectionFilters
): Promise<CollectionListResponse> => {
  const params = new URLSearchParams();

  if (filters?.type) {
    params.append('type', filters.type);
  }
  if (filters?.status) {
    params.append('status', filters.status);
  }

  const queryString = params.toString();
  const endpoint = queryString
    ? `${COLLECTION_BASE_PATH}?${queryString}`
    : COLLECTION_BASE_PATH;

  return fetchWithAuth(endpoint);
};

/**
 * Get a single collection with statistics
 *
 * @param collectionId - UUID of the collection
 * @returns Promise<CollectionWithStats>
 */
export const getCollection = async (
  collectionId: string
): Promise<CollectionWithStats> => {
  return fetchWithAuth(`${COLLECTION_BASE_PATH}/${collectionId}`);
};

/**
 * Update collection settings
 *
 * @param collectionId - UUID of the collection
 * @param settings - Settings to update
 * @returns Promise<Collection>
 */
export const updateCollectionSettings = async (
  collectionId: string,
  settings: CollectionUpdateRequest
): Promise<Collection> => {
  return fetchWithAuth(`${COLLECTION_BASE_PATH}/${collectionId}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
};

/**
 * Rename a collection
 */
export const renameCollection = async (
  collectionId: string,
  name: string
): Promise<Collection> => {
  return fetchWithAuth(`${COLLECTION_BASE_PATH}/${collectionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
};

/**
 * Delete a collection
 */
export const deleteCollection = async (
  collectionId: string
): Promise<{ id: string; status: string }> => {
  return fetchWithAuth(`${COLLECTION_BASE_PATH}/${collectionId}`, {
    method: 'DELETE',
  });
};

/**
 * Pause or resume monitoring for a collection.
 */
export const updateMonitoringStatus = async (
  collectionId: string,
  status: 'active' | 'paused'
): Promise<Collection> => {
  return fetchWithAuth(`${COLLECTION_BASE_PATH}/${collectionId}/monitoring-status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
};

// =============================================================================
// Records Functions
// =============================================================================

/**
 * List records in a collection with pagination and filtering
 *
 * @param collectionId - UUID of the collection
 * @param filters - Optional filters and pagination
 * @param page - Page number (default 1)
 * @param pageSize - Items per page (default 50)
 * @returns Promise<RecordsListResponse>
 */
export const listRecords = async (
  collectionId: string,
  filters?: RecordFilters,
  page: number = 1,
  pageSize: number = 50
): Promise<RecordsListResponse> => {
  const params = new URLSearchParams();

  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());

  if (filters?.search) {
    params.append('search', filters.search);
  }
  if (filters?.conductScoreMin !== undefined) {
    params.append('conductScoreMin', filters.conductScoreMin.toString());
  }
  if (filters?.conductScoreMax !== undefined) {
    params.append('conductScoreMax', filters.conductScoreMax.toString());
  }
  if (filters?.dateFrom) {
    params.append('dateFrom', filters.dateFrom);
  }
  if (filters?.dateTo) {
    params.append('dateTo', filters.dateTo);
  }
  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.includeDrafts) {
    params.append('includeDrafts', 'true');
  }
  if (filters?.eligibleComplaintsOnly) {
    params.append('eligibleComplaintsOnly', 'true');
  }
  if (filters?.vulnerableOnly) {
    params.append('vulnerableOnly', 'true');
  }
  if (filters?.sortBy) {
    params.append('sortBy', filters.sortBy);
  }
  if (filters?.sortOrder) {
    params.append('sortOrder', filters.sortOrder);
  }

  // Global Filter Strip fields (GO-35). Guard the preset so an unknown/legacy
  // value (e.g. a stale `all_time`) is never forwarded to the collection API.
  if (
    filters?.dateRangePreset &&
    (VALID_DATE_RANGE_PRESETS as readonly string[]).includes(filters.dateRangePreset)
  ) {
    params.append('dateRangePreset', filters.dateRangePreset);
  }
  if (filters?.dimensions?.length) {
    params.append('dimensions', filters.dimensions.join(','));
  }
  if (filters?.dimensionsLogic) {
    params.append('dimensionsLogic', filters.dimensionsLogic);
  }
  if (filters?.regulatoryFlags?.length) {
    params.append('regulatoryFlags', filters.regulatoryFlags.join(','));
  }
  if (filters?.regulatoryFlagsLogic) {
    params.append('regulatoryFlagsLogic', filters.regulatoryFlagsLogic);
  }

  const queryString = params.toString();
  return fetchWithAuth(`${COLLECTION_BASE_PATH}/${collectionId}/records?${queryString}`);
};

/**
 * Get a single record with full detail
 *
 * @param recordId - UUID of the record
 * @returns Promise<Record>
 */
export const getRecord = async (recordId: string): Promise<Record> => {
  return fetchWithAuth(`${RECORD_BASE_PATH}/${recordId}`);
};

/**
 * Confirm a record as a formal complaint
 *
 * @param recordId - UUID of the record
 * @param notes - Optional notes about the confirmation
 * @returns Promise<ConfirmComplaintResponse>
 */
export const confirmComplaint = async (
  recordId: string,
  notes?: string
): Promise<ConfirmComplaintResponse> => {
  return fetchWithAuth(`${RECORD_BASE_PATH}/${recordId}/confirm-complaint`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });
};

/**
 * Response type for reject complaint
 */
export interface RejectComplaintResponse {
  id: string;
  isConfirmedComplaint: boolean;
}

/**
 * Reject/unconfirm a record as a complaint
 *
 * Removes the human complaint confirmation, reverting the signal to
 * ML-detected-only state. Investigation section will be disabled
 * until re-confirmed.
 *
 * @param recordId - UUID of the record
 * @returns Promise<RejectComplaintResponse>
 */
export const rejectComplaint = async (
  recordId: string
): Promise<RejectComplaintResponse> => {
  return fetchWithAuth(`${RECORD_BASE_PATH}/${recordId}/reject-complaint`, {
    method: 'POST',
  });
};

/**
 * Reclassify a record with AI
 *
 * Triggers async reclassification of a record using the latest ML model.
 * The record ID maps to the underlying signal ID.
 *
 * @param recordId - UUID of the record (signal)
 * @returns Promise<ReclassifyRecordResponse>
 */
export const reclassifyRecord = async (
  recordId: string
): Promise<ReclassifyRecordResponse> => {
  return fetchWithAuth(`/v1/signals/${recordId}/reclassify`, {
    method: 'POST',
  });
};

/**
 * Response type for starting a simulation
 */
export interface StartSimulationResponse {
  id: string;
  signal_id: string;
  status: string;
  created_at: string;
}

/**
 * Start a new FOS Outcome Simulation for a record
 *
 * Creates a new simulation that analyzes the record against historical
 * FOS ombudsman decisions to predict likely outcome.
 *
 * @param recordId - UUID of the record (signal)
 * @returns Promise<StartSimulationResponse>
 */
export const startSimulation = async (
  recordId: string
): Promise<StartSimulationResponse> => {
  return fetchWithAuth('/v1/ombudsman-decisions/simulations', {
    method: 'POST',
    body: JSON.stringify({ signal_id: recordId }),
  });
};

// =============================================================================
// Service Object Export
// =============================================================================

/**
 * Collection Service object for easier imports
 */
export const collectionService = {
  // Collections
  list: listCollections,
  get: getCollection,
  updateSettings: updateCollectionSettings,
  rename: renameCollection,
  delete: deleteCollection,
  updateMonitoringStatus,

  // Records
  listRecords,
  getRecord,
  confirmComplaint,
  rejectComplaint,
  reclassifyRecord,
  startSimulation,
};
